// Typed fetch wrapper for the FileVouch API. Auth is supplied by the caller
// (Clerk's getToken()) — we don't import @clerk/clerk-react here so this
// module stays usable from any context (event handlers, effects, future
// tests).
//
// Endpoints we hit today:
//   POST /v1/billing/checkout  → { url }
//   POST /v1/billing/portal    → { url }
// Both already exist (Block 4). Webhook + stamps endpoints follow when the
// dashboard grows past the billing-only MVP.

export type ApiError = { error: string; message: string };

export class ApiCallError extends Error {
  status: number;
  body: ApiError | null;
  constructor(status: number, body: ApiError | null, message: string) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

async function call<T>(
  path: string,
  init: RequestInit,
  token: string | null,
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("content-type", "application/json");
  if (token) headers.set("authorization", `Bearer ${token}`);

  const res = await fetch(`${__FILEVOUCH_API_BASE__}${path}`, {
    ...init,
    headers,
    credentials: "omit",
  });

  if (!res.ok) {
    let body: ApiError | null = null;
    try {
      body = (await res.json()) as ApiError;
    } catch {
      // non-JSON error body — leave null
    }
    throw new ApiCallError(
      res.status,
      body,
      body?.message ?? `${res.status} ${res.statusText}`,
    );
  }
  return (await res.json()) as T;
}

export type BillingTier = {
  name: string;
  priceIds: string[];
  capabilities: Record<string, string | number | boolean>;
};

export type BillingTiersResponse = {
  tiers: BillingTier[];
  default: string;
};

// Public endpoint — no auth header. Cached for the session via the caller.
export async function fetchTiers(): Promise<BillingTiersResponse> {
  return call<BillingTiersResponse>(
    "/v1/billing/tiers",
    { method: "GET" },
    null,
  );
}

export type CheckoutInput = {
  priceId: string;
  successUrl: string;
  cancelUrl: string;
};

export async function startCheckout(
  input: CheckoutInput,
  token: string,
): Promise<{ url: string }> {
  return call<{ url: string }>(
    "/v1/billing/checkout",
    { method: "POST", body: JSON.stringify(input) },
    token,
  );
}

export async function startPortal(
  returnUrl: string,
  token: string,
): Promise<{ url: string }> {
  return call<{ url: string }>(
    "/v1/billing/portal",
    { method: "POST", body: JSON.stringify({ returnUrl }) },
    token,
  );
}

// ---------- Block 6 — account keys ----------

export type AccountKeyKind = "ed25519" | "es256";

export type AccountKey = {
  kid: string;
  kind: AccountKeyKind;
  public_key_pem: string;
  name: string | null;
  status: "active" | "revoked";
  created_at: number;
  revoked_at: number | null;
};

export type RegisterKeyInput = {
  kind: AccountKeyKind;
  public_key_pem: string;
  name?: string;
};

export type KeyRegisteredResponse = {
  kid: string;
  kind: AccountKeyKind;
  public_key_pem: string;
  name: string | null;
  status: "active";
  created_at: number;
};

export async function listKeys(token: string): Promise<AccountKey[]> {
  const res = await call<{ keys: AccountKey[] }>(
    "/v1/keys",
    { method: "GET" },
    token,
  );
  return res.keys;
}

export async function registerKey(
  input: RegisterKeyInput,
  token: string,
): Promise<KeyRegisteredResponse> {
  return call<KeyRegisteredResponse>(
    "/v1/keys",
    { method: "POST", body: JSON.stringify(input) },
    token,
  );
}

export async function revokeKey(kid: string, token: string): Promise<void> {
  // 204 → no JSON body; `call<T>` would crash on res.json(). Inline the
  // smaller fetch for this one endpoint instead of generalizing `call`.
  const headers = new Headers();
  headers.set("authorization", `Bearer ${token}`);
  const res = await fetch(`${__FILEVOUCH_API_BASE__}/v1/keys/${kid}`, {
    method: "DELETE",
    headers,
    credentials: "omit",
  });
  if (!res.ok) {
    let body: ApiError | null = null;
    try {
      body = (await res.json()) as ApiError;
    } catch {
      // non-JSON error body — leave null
    }
    throw new ApiCallError(
      res.status,
      body,
      body?.message ?? `${res.status} ${res.statusText}`,
    );
  }
}

// ---------- Block 7 — long-lived API keys ----------

export type ApiKeyScope = "stamp:write" | "verify:read";

export type ApiKey = {
  id: string;
  name: string;
  prefix: string;
  scopes: ApiKeyScope[];
  status: "active" | "revoked";
  created_at: number;
  last_used_at: number | null;
  revoked_at: number | null;
};

export type CreateApiKeyInput = {
  name: string;
  scopes: ApiKeyScope[];
};

export type ApiKeyCreated = ApiKey & {
  // Shown ONCE — the user must copy this before navigating away.
  plaintext: string;
};

export async function listApiKeys(token: string): Promise<ApiKey[]> {
  const res = await call<{ keys: ApiKey[] }>(
    "/v1/api-keys",
    { method: "GET" },
    token,
  );
  return res.keys;
}

export async function createApiKey(
  input: CreateApiKeyInput,
  token: string,
): Promise<ApiKeyCreated> {
  return call<ApiKeyCreated>(
    "/v1/api-keys",
    { method: "POST", body: JSON.stringify(input) },
    token,
  );
}

export async function revokeApiKey(id: string, token: string): Promise<void> {
  // 204 → no JSON body; same pattern as revokeKey above.
  const headers = new Headers();
  headers.set("authorization", `Bearer ${token}`);
  const res = await fetch(`${__FILEVOUCH_API_BASE__}/v1/api-keys/${id}`, {
    method: "DELETE",
    headers,
    credentials: "omit",
  });
  if (!res.ok) {
    let body: ApiError | null = null;
    try {
      body = (await res.json()) as ApiError;
    } catch {
      // non-JSON error — leave null
    }
    throw new ApiCallError(
      res.status,
      body,
      body?.message ?? `${res.status} ${res.statusText}`,
    );
  }
}
